import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { getSelectedDivision } from '@/helpers/utils/siteUtils';
import ContentStackImage from '../../common/contentStackImage/contentStackImage';

interface TagsProps {
  activePriceType: string | string[];
  overrideTagLabel?: string | string[];
  setIsTagLinkAvailable?: any;
}

// Returns true if the tag has no date constraints, or if today falls within its date range
const isTagDateValid = (tag: any): boolean => {
  try {
    if (!tag || typeof tag !== 'object') return false;
    const { startTime, endTime } = tag;
    if (!startTime && !endTime) return true;
    const now = new Date();
    if (startTime && now < new Date(startTime)) return false;
    if (endTime && now > new Date(endTime)) return false;
    return true;
  } catch {
    return true; // If date parsing fails, treat as always valid
  }
};

/**
 * Returns the first tag where priceTypeLabel or tagName equals value. No enabled/date/key checks.
 */
export function findTagByPriceTypeLabelOrTagName(
  tagsConfigurations: ProjectSettings['tagsConfigurations'],
  value: string | null | undefined,
): any | null {
  if (!value || !Array.isArray(tagsConfigurations)) return null;
  return tagsConfigurations.find((t: any) => t?.priceTypeLabel === value || t?.tagName === value) ?? null;
}

/**
 * Resolves the display tag config from project tag settings (same rules as the Tags component).
 * Returns null when no matching enabled tag or when tagName is empty.
 */
export function getResolvedTag(
  tagsConfigurations: ProjectSettings['tagsConfigurations'],
  activePriceType: string | string[] | undefined,
  overrideTagLabel?: string | string[],
): any | null {
  try {
    if (!tagsConfigurations || !Array.isArray(tagsConfigurations) || tagsConfigurations.length === 0) {
      return null;
    }
    if (!activePriceType) return null;

    let tag;

    if (Array.isArray(activePriceType)) {
      for (const currentTag of tagsConfigurations) {
        if (
          currentTag?.enabled &&
          currentTag?.tagKey &&
          activePriceType?.includes(currentTag?.tagKey) &&
          !currentTag?.priceTypeLabel
        ) {
          tag = currentTag;
          break;
        }
      }
    } else {
      tag = tagsConfigurations?.find(
        (t: any) => t?.tagKey === activePriceType && t?.enabled !== false && !t?.priceTypeLabel,
      );
    }

    if (overrideTagLabel) {
      try {
        const labels = Array.isArray(overrideTagLabel)
          ? overrideTagLabel.filter(Boolean)
          : overrideTagLabel
              ?.split(',')
              ?.map((label: string) => label.trim())
              .filter(Boolean);

        if (labels.length > 0) {
          const activeTagKey = tag?.tagKey;
          const activeKeys = Array.isArray(activePriceType) ? activePriceType : [activePriceType];

          const matchingTags = tagsConfigurations
            .filter(
              (t: any) =>
                t?.enabled !== false &&
                t?.priceTypeLabel &&
                labels.includes(t.priceTypeLabel) &&
                activeKeys.includes(t?.tagKey) &&
                isTagDateValid(t),
            )
            .sort((a: any, b: any) => tagsConfigurations.indexOf(a) - tagsConfigurations.indexOf(b));

          const defaultTag = activeTagKey
            ? tagsConfigurations.find(
                (t: any) =>
                  t?.enabled !== false && t?.tagKey === activeTagKey && !t?.priceTypeLabel && isTagDateValid(t),
              )
            : undefined;

          if (matchingTags.length > 0) {
            tag = matchingTags[0];
          } else if (defaultTag) {
            const defaultIndex = tagsConfigurations.indexOf(defaultTag);
            const activeIndex = tagsConfigurations.indexOf(tag);
            if (activeIndex === -1 || defaultIndex < activeIndex) {
              tag = defaultTag;
            }
          }
        }
      } catch {
        // Override resolution failed — continue with the active tag as-is
      }
    }

    if (!tag || tag?.enabled === false) return null;

    const tagName = tag?.tagName || '';
    if (!tagName) return null;

    return tag;
  } catch {
    return null;
  }
}

const Tags = ({ activePriceType, overrideTagLabel, setIsTagLinkAvailable }: TagsProps) => {
  const projectSettings: ProjectSettings = useProjSettings();
  const siteTagsConfigurations = projectSettings?.tagsConfigurations;
  const selectedDivision = getSelectedDivision() ?? 'ZB';
  const tagsConfigurations =
    siteTagsConfigurations?.find((tag: any) => `${tag?.SITE_ID}` === selectedDivision)?.TAGS ?? [];

  try {
    // Fail-safe: Check if tagsConfigurations exists and is an array
    if (!tagsConfigurations || !Array.isArray(tagsConfigurations) || tagsConfigurations.length === 0) {
      return null;
    }

    // Fail-safe: Return null if activePriceType is missing
    if (!activePriceType) return null;

    // Fail-safe: Handle activePriceType as array or string
    let tag;

    if (Array.isArray(activePriceType)) {
      for (const currentTag of tagsConfigurations) {
        if (
          currentTag?.enabled &&
          currentTag?.tagKey &&
          activePriceType?.includes(currentTag?.tagKey) &&
          !currentTag?.priceTypeLabel
        ) {
          tag = currentTag;
          break;
        }
      }
    } else {
      tag = tagsConfigurations?.find(
        (t: any) => t?.tagKey === activePriceType && t?.enabled !== false && !t?.priceTypeLabel,
      );
    }

    if (overrideTagLabel) {
      try {
        const labels = Array.isArray(overrideTagLabel)
          ? overrideTagLabel.filter(Boolean)
          : overrideTagLabel
              ?.split(',')
              ?.map((label: string) => label.trim())
              .filter(Boolean);

        if (labels.length > 0) {
          const activeTagKey = tag?.tagKey;

          // 1. Tags matching priceTypeLabel with valid dates, sorted by config order
          const activeKeys = Array.isArray(activePriceType) ? activePriceType : [activePriceType];
          const matchingTags = tagsConfigurations
            .filter(
              (t: any) =>
                t?.enabled !== false &&
                t?.priceTypeLabel &&
                labels.includes(t.priceTypeLabel) &&
                activeKeys.includes(t?.tagKey) &&
                isTagDateValid(t),
            )
            .sort((a: any, b: any) => tagsConfigurations.indexOf(a) - tagsConfigurations.indexOf(b));

          // 2. Default fallback: same tagKey, null/empty priceTypeLabel, date valid
          const defaultTag = activeTagKey
            ? tagsConfigurations.find(
                (t: any) =>
                  t?.enabled !== false && t?.tagKey === activeTagKey && !t?.priceTypeLabel && isTagDateValid(t),
              )
            : undefined;

          if (matchingTags.length > 0) {
            tag = matchingTags[0];
          } else if (defaultTag) {
            const defaultIndex = tagsConfigurations.indexOf(defaultTag);
            const activeIndex = tagsConfigurations.indexOf(tag);
            if (activeIndex === -1 || defaultIndex < activeIndex) {
              tag = defaultTag;
            }
          }
        }
      } catch {
        // Override resolution failed — continue with the active tag as-is
      }
    }

    // Fail-safe: Return null if tag not found or disabled
    if (!tag || tag?.enabled === false) return null;

    const tagName = tag?.tagName || '';
    const icon = tag?.icon && typeof tag?.icon === 'string' ? tag?.icon : null;
    const link = tag?.link;
    const color = tag?.color || '#000000';

    if (!tagName) return null;

    const content = (
      <section className="mt-1 flex items-center">
        {icon && <ContentStackImage className="mr-1" src={icon} imageWidth={12} imageHeight={12} alt={tagName} />}
        <p
          style={{
            fontWeight: 700,
            fontStyle: 'normal',
            fontSize: '12px',
            lineHeight: '130%',
            letterSpacing: '0%',
            color,
          }}
        >
          {tagName}
        </p>
      </section>
    );

    if (link && link !== '#') {
      setIsTagLinkAvailable?.(true);
      return (
        <a
          href={link}
          className="inline-block cursor-pointer transition hover:underline group-hover/clearance:underline"
          style={{ color }}
        >
          {content}
        </a>
      );
    }

    return content;
  } catch {
    return null;
  }
};

interface TagByPriceTypeLabelOrNameProps {
  value: string | null | undefined;
  setIsTagLinkAvailable?: any;
}

/** Same visual as Tags, but resolves the tag only by priceTypeLabel or tagName (see findTagByPriceTypeLabelOrTagName). */
export const TagByPriceTypeLabelOrName = ({ value, setIsTagLinkAvailable }: TagByPriceTypeLabelOrNameProps) => {
  const projectSettings: ProjectSettings = useProjSettings();
  const siteTagsConfigurations = projectSettings?.tagsConfigurations;
  const selectedDivision = getSelectedDivision() ?? 'ZB';
  const tagsConfigurations =
    siteTagsConfigurations?.find((tag: any) => `${tag?.SITE_ID}` === selectedDivision)?.TAGS ?? [];

  const tag = findTagByPriceTypeLabelOrTagName(tagsConfigurations, value);

  if (!tag) return null;

  const tagName = tag?.tagName || '';
  const icon = tag?.icon && typeof tag?.icon === 'string' ? tag?.icon : null;
  const link = tag?.link;
  const color = tag?.color || '#000000';

  if (!tagName) return null;

  const content = (
    <section className="mt-1 flex items-center">
      {icon && <ContentStackImage className="mr-1" src={icon} imageWidth={12} imageHeight={12} alt={tagName} />}
      <p
        style={{
          fontWeight: 700,
          fontStyle: 'normal',
          fontSize: '12px',
          lineHeight: '130%',
          letterSpacing: '0%',
          color,
        }}
      >
        {tagName}
      </p>
    </section>
  );

  if (link && link !== '#') {
    setIsTagLinkAvailable?.(true);
    return (
      <a
        href={link}
        className="inline-block cursor-pointer transition hover:underline group-hover/clearance:underline"
        style={{ color }}
      >
        {content}
      </a>
    );
  }

  return content;
};

export default Tags;
